import { getCoreUtils } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppTopBarView, AppTopStatus } from "./AppTopBar";
import { AppMenus } from "./AppMenu";
import { AppLiveText } from "./AppLiveText";

// AppTopBarRun - the browser half of the top bar (`AppTopBar` holds the rules and the markup). `Mount` fills the shell's
// `<header class="bar">` once (the wordmark the shell carries is replaced by the same wordmark inside the bar's line);
// `Status` writes the connection line; `ShowSearch` puts the chat search into the bar on the Chats view and hides it
// elsewhere. ⚠ THE SEARCH INPUT IS MADE ONCE AND KEPT: repainting it would lose focus and the caret (the reason the list
// and the box were always separate elements), so a later `ShowSearch` only shows the slot again.
// ⛔ No inline style (the CSP) - `hidden` and classes only.

/** A part of the status line (the browser's element, or a test's). */
export interface ITopStatusPart {
    textContent: string | null;
}

/** The status line's element as `PaintStatus` touches it. */
export interface ITopStatusEl {
    hidden: boolean;
    className: string;
    title: string;
    querySelector(Selector: string): ITopStatusPart | null;
}

export class AppTopBarRun {
    /** The header holds the bar (and so the chat search) - `AppViews.Chats` then leaves the box out of the page. */
    public Mounted: boolean = false;

    /** Fill the header once; the menus get their behaviour (`AppMenus`, one listener set per page). */
    public Mount = (Theme: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Mounted === true) { return; }
            const Bars: HTMLCollectionOf<Element> = document.getElementsByClassName("bar");
            if (Bars.length === 0) { return; }
            (Bars[0] as HTMLElement).innerHTML = AppTopBarView.Html(Theme);
            this.Mounted = true;
            AppMenus.ForPage();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTopBarRun.Mount");
        }
    };

    /** The connection line: dot, words, tooltip; hidden when there is nothing to say. */
    public Status = (Now: AppTopStatus): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const El: HTMLElement | null = document.getElementById(AppTopBarView.StatusId);
            if (El === null) { return; }
            AppTopBarRun.PaintStatus(El as unknown as ITopStatusEl, Now);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTopBarRun.Status");
        }
    };

    /**
     * Write the status into its element. ⛔ THE LIVE REGION (`.sword`) IS WRITTEN ONLY WHEN THE STATE WORD CHANGES - the
     * visible "updated 12 min ago" (`.stxt`, hidden from a screen reader) and `.swhen` move every minute and are not live.
     */
    public static PaintStatus = (El: ITopStatusEl, Now: AppTopStatus): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            // ⛔ NOTHING IS WRITTEN THAT DID NOT CHANGE (2026-10-01): the feed now answers early on a state change, so this
            // runs far more often than once per 15 s - and a class or a title set to its own value is still a DOM write.
            if (Now.Kind.length === 0) { if (El.hidden !== true) { El.hidden = true; } return; }
            if (El.hidden !== false) { El.hidden = false; }
            const Cls: string = "topstatus st-" + Now.Kind;
            if (El.className !== Cls) { El.className = Cls; }
            if (El.title !== Now.Tip) { El.title = Now.Tip; }
            AppTopBarRun.Put(El.querySelector(".stxt"), Now.Text);
            AppTopBarRun.Put(El.querySelector(".sword"), Now.Word);
            AppTopBarRun.Put(El.querySelector(".swhen"), Now.When);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTopBarRun.PaintStatus");
        }
    };

    /** Text into a part - only when it differs (a live region re-written with the same words is announced again). */
    private static Put = (Part: ITopStatusPart | null, Text: string): void => {
        AppLiveText.Set(Part, Text, "AppTopBarRun.Put");
    };

    /**
     * The chat search in the bar (`Markup` = `AppViews.SearchBox`), or hidden. Answers the input when it was MADE now (so
     * the caller wires it once), else null. The input already there keeps its value, focus and caret.
     */
    public ShowSearch = (On: boolean, Markup: string): HTMLInputElement | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            const Slot: HTMLElement | null = document.getElementById(AppTopBarView.SearchId);
            if (Slot === null) { return null; }
            Slot.hidden = On === false;
            document.body.classList.toggle("wac-hasfind", On === true);
            if (On === false || Slot.querySelector("input") !== null) { return null; }
            // ⭐ The shortcut's keycaps ride beside the field (decoration, wide widths only - `AppTopBarView.SearchKeys`).
            Slot.innerHTML = Markup + AppTopBarView.SearchKeys(/Mac|iPhone|iPad/.test(String(navigator.platform)));
            return Slot.querySelector("input") as HTMLInputElement | null;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTopBarRun.ShowSearch");
            return null;
        }
    };
}
